import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Trash2, PlayCircle, CheckCircle, MapPin, Activity } from 'lucide-react';
import { AssignmentTimeline } from './AssignmentTimeline';
import type { Technician, Assignment } from '@shared/types';

interface EditAssignmentModalProps {
    initialAssignment: Assignment;
    technicians: Technician[];
    initialTechnicianIds: string[];
    isOpen: boolean;
    onClose: () => void;
    onSave: (updatedAssignment: Assignment, technicianIds: string[]) => void;
    onDelete: () => void;
    onStartWorksite?: (assignmentId: string) => void;
    onFinishWorksite?: (assignmentId: string) => void;
    services?: string[];
}

export const EditAssignmentModal: React.FC<EditAssignmentModalProps> = ({
    initialAssignment,
    technicians,
    initialTechnicianIds,
    isOpen,
    onClose,
    onSave,
    onDelete,
    onStartWorksite,
    onFinishWorksite,
}) => {
    const [clientName, setClientName] = useState(initialAssignment.clientName);
    const [startDate, setStartDate] = useState(initialAssignment.startDate);
    const [endDate, setEndDate] = useState(initialAssignment.endDate);
    const [task, setTask] = useState(initialAssignment.task || '');
    const [address, setAddress] = useState(initialAssignment.address || '');
    const [city, setCity] = useState(initialAssignment.city || '');
    const [postalCode, setPostalCode] = useState(initialAssignment.postalCode || '');
    const [contactName, setContactName] = useState(initialAssignment.contactName || '');
    const [contactPhone, setContactPhone] = useState(initialAssignment.contactPhone || '');
    const [startTime, setStartTime] = useState(initialAssignment.startTime || '');
    const [endTime, setEndTime] = useState(initialAssignment.endTime || '');
    const [includeWeekends, setIncludeWeekends] = useState(initialAssignment.includeWeekends || false);
    const [type, setType] = useState(initialAssignment.type || 'Maintenance');
    const [remarque, setRemarque] = useState(initialAssignment.remarque || '');
    const [selectedTechIds, setSelectedTechIds] = useState<string[]>(initialTechnicianIds);
    const [status, setStatus] = useState<string>(initialAssignment.status || 'todo');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');

    if (!isOpen) return null;

    const toggleTechnician = (techId: string) => {
        setSelectedTechIds(prev =>
            prev.includes(techId) ? prev.filter(id => id !== techId) : [...prev, techId]
        );
    };

    const handleSave = () => {
        if (!clientName.trim() || selectedTechIds.length === 0) return;

        const updatedAssignment: Assignment = {
            ...initialAssignment,
            clientName,
            startDate,
            endDate,
            task: task || undefined,
            address: address || undefined,
            city: city || undefined,
            postalCode: postalCode || undefined,
            contactName: contactName || undefined,
            contactPhone: contactPhone || undefined,
            startTime: startTime || undefined,
            endTime: endTime || undefined,
            includeWeekends,
            type: type || undefined,
            remarque: remarque || undefined,
            status: status as any, // Allow manual override
            startedAt: status === 'in-progress' && !initialAssignment.startedAt ? new Date().toISOString() : initialAssignment.startedAt,
            completedAt: (status === 'completed-by-tech' || status === 'done') && !initialAssignment.completedAt ? new Date().toISOString() : initialAssignment.completedAt,
            closedAt: status === 'closed' && !initialAssignment.closedAt ? new Date().toISOString() : initialAssignment.closedAt,
            closedBy: initialAssignment.closedBy,
        };

        onSave(updatedAssignment, selectedTechIds);
        onClose();
    };

    const handleDelete = () => {
        onDelete();
        onClose();
    };

    // Helper to get color for select
    const getStatusColor = (s: string) => {
        switch (s) {
            case 'pending': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
            case 'todo': return 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20';
            case 'in-progress': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
            case 'completed-by-tech':
            case 'done': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
            case 'blocked': return 'bg-red-500/10 text-red-500 border-red-500/20';
            case 'closed': return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
            default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-[#0f172a] border border-white/10 rounded-2xl w-full max-w-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col max-h-[90vh] ring-1 ring-white/5"
            >
                {/* Modern Header */}
                <div className="relative p-6 pt-8 pb-6 border-b border-white/5 bg-gradient-to-b from-white/5 to-transparent">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 text-white/30 hover:text-white hover:bg-white/10 rounded-full transition-all"
                    >
                        <X size={20} />
                    </button>

                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-3">
                            <span className="text-[10px] font-mono tracking-widest uppercase text-accent/80 bg-accent/10 px-2 py-0.5 rounded border border-accent/20">
                                {initialAssignment.worksiteId || 'NOUVEAU'}
                            </span>

                            <div className="relative group">
                                <select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    className={`appearance-none cursor-pointer text-[10px] font-bold uppercase px-2 py-0.5 pr-6 rounded border outline-none hover:brightness-110 transition-all ${getStatusColor(status)}`}
                                >
                                    <option className="bg-slate-900 text-yellow-500" value="pending">En attente</option>
                                    <option className="bg-slate-900 text-indigo-400" value="todo">Planifié</option>
                                    <option className="bg-slate-900 text-blue-500" value="in-progress">En cours</option>
                                    <option className="bg-slate-900 text-emerald-500" value="completed-by-tech">Terminé</option>
                                    <option className="bg-slate-900 text-red-500" value="blocked">Bloqué</option>
                                    <option className="bg-slate-900 text-slate-400" value="closed">Clôturé</option>
                                </select>
                                <div className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-50">
                                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M6 9l6 6 6-6" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <input
                            type="text"
                            value={clientName}
                            onChange={e => setClientName(e.target.value)}
                            placeholder="Nom du Chantier"
                            className="bg-transparent text-3xl font-bold text-white placeholder-white/20 focus:outline-none w-full"
                        />
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                            <MapPin size={14} className="text-gray-500" />
                            <input
                                type="text"
                                value={city}
                                onChange={e => setCity(e.target.value)}
                                placeholder="Ville"
                                className="bg-transparent focus:outline-none hover:text-white focus:text-white transition-colors w-32"
                            />
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-6 px-6 border-b border-white/5 bg-white/[0.02]">
                    <button
                        onClick={() => setActiveTab('details')}
                        className={`py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'details' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-white'}`}
                    >
                        Détails
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'history' ? 'border-accent text-accent' : 'border-transparent text-gray-400 hover:text-white'}`}
                    >
                        <Activity size={14} />
                        Historique
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                    {activeTab === 'details' ? (
                        <div className="space-y-8">
                            {/* Section 1: Timing & Techs */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Planning</h4>
                                    <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5 space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <label className="text-[10px] text-gray-400 uppercase">Début</label>
                                                <input
                                                    type="date"
                                                    value={startDate}
                                                    onChange={e => setStartDate(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:bg-white/10 focus:border-primary/50 transition-all outline-none"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] text-gray-400 uppercase">Fin</label>
                                                <input
                                                    type="date"
                                                    value={endDate}
                                                    onChange={e => setEndDate(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:bg-white/10 focus:border-primary/50 transition-all outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                type="time"
                                                value={startTime}
                                                onChange={e => setStartTime(e.target.value)}
                                                className="bg-transparent text-xs text-gray-300 border-b border-white/10 focus:border-primary focus:text-white outline-none py-1"
                                            />
                                            <input
                                                type="time"
                                                value={endTime}
                                                onChange={e => setEndTime(e.target.value)}
                                                className="bg-transparent text-xs text-gray-300 border-b border-white/10 focus:border-primary focus:text-white outline-none py-1"
                                            />
                                        </div>
                                        <label className="flex items-center gap-2 cursor-pointer group">
                                            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${includeWeekends ? 'bg-primary border-primary' : 'border-white/20 group-hover:border-white/40'}`}>
                                                {includeWeekends && <CheckCircle size={10} className="text-white" />}
                                            </div>
                                            <input type="checkbox" checked={includeWeekends} onChange={e => setIncludeWeekends(e.target.checked)} className="hidden" />
                                            <span className="text-xs text-gray-400 group-hover:text-gray-300">Inclure week-ends</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Équipe</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {technicians.map(tech => {
                                            const isSelected = selectedTechIds.includes(tech.id);
                                            return (
                                                <button
                                                    key={tech.id}
                                                    onClick={() => toggleTechnician(tech.id)}
                                                    className={`
                                                relative group flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition-all duration-200
                                                ${isSelected
                                                            ? 'bg-white/10 border-white/20'
                                                            : 'bg-transparent border-white/5 hover:border-white/20 opacity-60 hover:opacity-100'}
                                            `}
                                                >
                                                    <div
                                                        className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shadow-sm"
                                                        style={{ backgroundColor: tech.color }}
                                                    >
                                                        {tech.name.charAt(0)}
                                                    </div>
                                                    <span className={`text-xs ${isSelected ? 'text-white font-medium' : 'text-gray-400'}`}>
                                                        {tech.name}
                                                    </span>
                                                    {isSelected && (
                                                        <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-[#0f172a]" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Details Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="md:col-span-2 space-y-4">
                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Détails Mission</h4>
                                    <div className="space-y-3">
                                        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-0 overflow-hidden">
                                            <select
                                                value={type}
                                                onChange={e => setType(e.target.value)}
                                                className="w-full bg-transparent p-3 text-sm text-white focus:bg-white/5 outline-none border-b border-white/5 appearance-none"
                                            >
                                                <option className="bg-slate-800" value="Maintenance">🔧 Maintenance</option>
                                                <option className="bg-slate-800" value="Reseau">📡 Réseau</option>
                                                <option className="bg-slate-800" value="Électricité">⚡ Électricité</option>
                                                <option className="bg-slate-800" value="Plomberie">💧 Plomberie</option>
                                                <option className="bg-slate-800" value="Climatisation">❄️ Climatisation</option>
                                            </select>
                                            <textarea
                                                value={task}
                                                onChange={e => setTask(e.target.value)}
                                                placeholder="Description de la tâche..."
                                                className="w-full bg-transparent p-3 text-sm text-gray-300 placeholder-gray-600 focus:text-white min-h-[80px] outline-none resize-none"
                                            />
                                        </div>
                                        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3">
                                            <label className="text-[10px] text-gray-500 uppercase mb-1 block">Remarques Internes</label>
                                            <textarea
                                                value={remarque}
                                                onChange={e => setRemarque(e.target.value)}
                                                placeholder="Notes privées (codes, accès...)"
                                                className="w-full bg-transparent text-sm text-yellow-100/80 placeholder-gray-600 focus:text-yellow-100 min-h-[60px] outline-none resize-none"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Localisation</h4>
                                    <div className="space-y-2">
                                        <input
                                            type="text"
                                            value={address}
                                            onChange={e => setAddress(e.target.value)}
                                            placeholder="Adresse"
                                            className="w-full bg-transparent border-b border-white/10 py-2 text-sm text-white focus:border-white/30 outline-none transition-colors"
                                        />
                                        <div className="grid grid-cols-3 gap-2">
                                            <input
                                                type="text"
                                                value={postalCode}
                                                onChange={e => setPostalCode(e.target.value)}
                                                placeholder="CP"
                                                className="col-span-1 bg-transparent border-b border-white/10 py-2 text-sm text-white focus:border-white/30 outline-none"
                                            />
                                            <div className="col-span-2 py-2 text-sm text-gray-400 truncate pointer-events-none">
                                                {city || 'Ville...'}
                                            </div>
                                        </div>
                                        <div className="pt-4">
                                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Contact</h4>
                                            <div className="space-y-2">
                                                <input
                                                    type="text"
                                                    value={contactName}
                                                    onChange={e => setContactName(e.target.value)}
                                                    placeholder="Nom du contact"
                                                    className="w-full bg-transparent border-b border-white/10 py-1 text-sm text-white outline-none"
                                                />
                                                <input
                                                    type="text"
                                                    value={contactPhone}
                                                    onChange={e => setContactPhone(e.target.value)}
                                                    placeholder="Téléphone"
                                                    className="w-full bg-transparent border-b border-white/10 py-1 text-sm text-white outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <AssignmentTimeline assignmentId={initialAssignment.id} />
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-white/5 bg-black/20 backdrop-blur-sm flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowDeleteConfirm(true)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors text-xs font-medium"
                        >
                            <Trash2 size={14} />
                            <span className="hidden sm:inline">Supprimer</span>
                        </button>

                        {/* Status Actions */}
                        {onStartWorksite && initialAssignment.status === 'pending' && (
                            <button
                                onClick={() => { onStartWorksite(initialAssignment.id); onClose(); }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-blue-400 hover:bg-blue-500/10 hover:text-blue-300 transition-colors text-xs font-medium"
                            >
                                <PlayCircle size={14} />
                                <span className="hidden sm:inline">Démarrer</span>
                            </button>
                        )}
                        {onFinishWorksite && initialAssignment.status === 'in-progress' && (
                            <button
                                onClick={() => { onFinishWorksite(initialAssignment.id); onClose(); }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-green-400 hover:bg-green-500/10 hover:text-green-300 transition-colors text-xs font-medium"
                            >
                                <CheckCircle size={14} />
                                <span className="hidden sm:inline">Terminer</span>
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors text-sm"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!clientName || selectedTechIds.length === 0}
                            className="px-6 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg shadow-primary/20 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            Enregistrer
                        </button>
                    </div>
                </div>
            </motion.div>

            {/* Delete Overlay */}
            {showDeleteConfirm && (
                <div className="absolute inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 rounded-2xl">
                    <div className="bg-[#1e293b] border border-red-500/30 rounded-xl p-6 max-w-sm w-full text-center shadow-2xl">
                        <div className="w-12 h-12 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center mx-auto mb-4">
                            <Trash2 size={24} />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">Supprimer ce chantier ?</h3>
                        <p className="text-sm text-gray-400 mb-6">Cette action est irréversible et retirera le chantier de tous les plannings.</p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium"
                            >
                                Confirmer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
